import { getDb, type OutboxPhoto } from './dexie';
import type { DiaClient } from './client';
import type { Uuid } from './types';

/**
 * The photo pipeline (Section 11 and Section 2 item 3).
 *
 * iOS has no Background Sync, so a photo is resized and uploaded the moment it
 * is taken, never batched. If the upload fails, the blob stays in IndexedDB and
 * retries on the next tick. The checklist tick that owns the photo is never
 * blocked by it: the path is computed on the phone at capture time and written
 * into the row straight away, so the row lands with the network back for one
 * second and the photo catches up whenever it can.
 */

export const MAX_EDGE_PX = 1600;
export const JPEG_QUALITY = 0.82;

/** Long edge down to maxEdge, aspect kept, never scaled up. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge = MAX_EDGE_PX,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export type PhotoPathParts = {
  stayId?: Uuid | null;
  runId?: Uuid | null;
  itemId?: Uuid | null;
  requestId?: Uuid | null;
  /** The capture moment, so two photos of the same item never collide. */
  at?: Date;
  ext?: string;
};

/**
 * stay_id/run_id/item_id-timestamp.jpg, as Section 5 spells it. The first
 * folder is the stay, because the storage policy reads it to decide whether a
 * guest may touch the object.
 */
export function photoPath({
  stayId,
  runId,
  itemId,
  requestId,
  at = new Date(),
  ext = 'jpg',
}: PhotoPathParts): string {
  const stamp = at.toISOString().replace(/[:.]/g, '-');
  const folders = [stayId ?? 'sin-estadia', runId ?? requestId ?? 'suelto'];
  const stem = itemId ?? 'foto';
  return `${folders.join('/')}/${stem}-${stamp}.${ext}`;
}

type CanvasLike = {
  width: number;
  height: number;
  getContext(id: '2d'): { drawImage(image: ImageBitmap, x: number, y: number, w: number, h: number): void } | null;
  convertToBlob?: (options: { type: string; quality: number }) => Promise<Blob>;
  toBlob?: (cb: (blob: Blob | null) => void, type: string, quality: number) => void;
};

function makeCanvas(width: number, height: number): CanvasLike | null {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height) as unknown as CanvasLike;
  if (typeof document !== 'undefined') {
    const el = document.createElement('canvas');
    el.width = width;
    el.height = height;
    return el as unknown as CanvasLike;
  }
  return null;
}

async function canvasToBlob(canvas: CanvasLike, type: string, quality: number): Promise<Blob> {
  if (canvas.convertToBlob) return canvas.convertToBlob({ type, quality });
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob?.(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas produced no blob'))),
      type,
      quality,
    );
  });
}

/**
 * Resize to about 1600 px on the long edge and hand back a JPEG.
 *
 * HEIC: Safari on iOS decodes HEIC in createImageBitmap, so the canvas step
 * converts it for free. heic2any is only worth adding if a device turns up that
 * cannot (Section 3), and no dependency is added for a device we have not met.
 *
 * If the browser has neither an OffscreenCanvas nor a document (a unit test,
 * a worker without canvas), the original file comes back untouched rather than
 * throwing. A large photo is better than a lost one.
 */
export async function resizeImage(file: File, maxEdge = MAX_EDGE_PX): Promise<File> {
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const target = fitWithin(bitmap.width, bitmap.height, maxEdge);
  const canvas = makeCanvas(target.width, target.height);
  if (!canvas) {
    bitmap.close?.();
    return file;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    return file;
  }

  ctx.drawImage(bitmap, 0, 0, target.width, target.height);
  bitmap.close?.();

  try {
    const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

export type QueuePhotoInput = {
  id: Uuid;
  bucket: string;
  path: string;
  blob: Blob;
};

/** Puts a photo in the local queue. Upload is a separate, non blocking step. */
export async function queuePhoto(input: QueuePhotoInput, now = Date.now()): Promise<OutboxPhoto> {
  const row: OutboxPhoto = {
    id: input.id,
    bucket: input.bucket,
    path: input.path,
    blob: input.blob,
    bytes: input.blob.size,
    createdAt: now,
    attempts: 0,
    nextAttemptAt: now,
    status: 'pending',
  };
  await getDb().photos.put(row);
  return row;
}

/** When a photo is next due, or null when none is waiting. See nextRetryAt. */
export async function nextPhotoRetryAt(): Promise<number | null> {
  const waiting = await getDb().photos.where('status').anyOf('pending', 'failed').toArray();
  if (waiting.length === 0) return null;
  return waiting.reduce((soonest, p) => Math.min(soonest, p.nextAttemptAt), Infinity);
}

/** Same idea as the outbox: the network is back, stop waiting. */
export async function resetPhotoBackoff(now = Date.now()): Promise<number> {
  const db = getDb();
  const waiting = await db.photos.filter((p) => p.nextAttemptAt > now).toArray();
  await Promise.all(waiting.map((p) => db.photos.update(p.id, { nextAttemptAt: now })));
  return waiting.length;
}

/**
 * How long an `uploading` row may sit before a drain assumes the upload that
 * owns it is gone. One minute is far longer than a 1600 px JPEG takes even on a
 * bad Starlink minute, and a drain never runs two at once, so this only ever
 * catches an upload whose process died.
 */
export const STALE_UPLOAD_MS = 60_000;

/**
 * Puts photos stuck on `uploading` back in the queue (defect D3, Section 13
 * item 2).
 *
 * Killing the app mid upload left the row as `uploading` with zero attempts.
 * `drainPhotos` only ever picked up `pending` and `failed`, so that photo was
 * never tried again: the tick it belonged to landed pointing at a storage path
 * that would 404 for the rest of its life, and nothing anywhere said so.
 *
 * On a fresh start nothing can be in flight, so `staleAfterMs` is 0 and every
 * `uploading` row is recovered. Inside a running drain it is STALE_UPLOAD_MS,
 * so a slow upload is left alone.
 *
 * Recovering is not re-uploading: the path was computed at capture time and the
 * upload is an upsert on that exact path, so a photo that did land before the
 * app died is written once more to the same object rather than duplicated.
 */
export async function recoverStalePhotos(now = Date.now(), staleAfterMs = 0): Promise<number> {
  const db = getDb();
  const stuck = await db.photos.where('status').equals('uploading').toArray();
  const stale = stuck.filter((p) => now - (p.startedAt ?? p.createdAt) >= staleAfterMs);

  await Promise.all(
    stale.map((p) =>
      db.photos.update(p.id, {
        status: 'pending',
        // It goes now, not after a backoff it never earned.
        nextAttemptAt: now,
        startedAt: undefined,
        lastError: 'interrupted mid upload, queued again',
      }),
    ),
  );

  return stale.length;
}

export type PhotoDrainResult = {
  uploaded: number;
  failed: number;
  remaining: number;
  /** Photos found stuck on `uploading` and put back in the queue. */
  recovered: number;
};

/**
 * Uploads every photo whose backoff has elapsed. One failure does not stop the
 * others, and nothing here is ever awaited by the row drain.
 */
export async function drainPhotos(
  client: DiaClient,
  now = Date.now(),
  backoff = photoBackoff,
): Promise<PhotoDrainResult> {
  const db = getDb();
  const recovered = await recoverStalePhotos(now, STALE_UPLOAD_MS);

  const due = (await db.photos.where('status').anyOf('pending', 'failed').toArray())
    .filter((p) => p.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt - b.createdAt);

  let uploaded = 0;
  let failed = 0;

  for (const photo of due) {
    await db.photos.update(photo.id, { status: 'uploading', startedAt: now });
    try {
      const { error } = await client.storage
        .from(photo.bucket)
        .upload(photo.path, photo.blob, { contentType: photo.blob.type || 'image/jpeg', upsert: true });
      if (error) throw error;
      // The blob is the biggest thing in IndexedDB. It leaves the moment the
      // island has it.
      await db.photos.delete(photo.id);
      uploaded += 1;
    } catch (err) {
      const attempts = photo.attempts + 1;
      await db.photos.update(photo.id, {
        status: 'failed',
        attempts,
        nextAttemptAt: now + backoff(attempts),
        startedAt: undefined,
        lastError: err instanceof Error ? err.message : String(err),
      });
      failed += 1;
    }
  }

  return { uploaded, failed, remaining: await db.photos.count(), recovered };
}

/** 5 s, 10 s, 20 s, up to 10 minutes. Photos are patient, they are not urgent. */
export function photoBackoff(attempts: number): number {
  return Math.min(10 * 60_000, 5_000 * 2 ** Math.max(0, attempts - 1));
}
